// pages/Feedback.tsx
import React, { useState } from "react";
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  LightBulbIcon,
  BugAntIcon,
  QuestionMarkCircleIcon,
  TagIcon,
  ClockIcon,
  UserCircleIcon,
  StarIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

const Feedback: React.FC = () => {
  const [feedbackType, setFeedbackType] = useState<
    "bug" | "suggestion" | "question" | "other"
  >("suggestion");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState<"low" | "medium" | "high">("medium");
  const [email, setEmail] = useState("");
  const [includeScreenshot, setIncludeScreenshot] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const categories = [
    "Dashboard",
    "Performance Reports",
    "Ad Units",
    "Settings",
    "Billing",
    "API",
    "Mobile App",
    "Other",
  ];

  const previousFeedback = [
    {
      id: 1,
      title: "Dark mode support",
      type: "suggestion",
      status: "in-progress",
      date: "2024-01-10",
      comments: 3,
    },
    {
      id: 2,
      title: "Export data feature not working",
      type: "bug",
      status: "resolved",
      date: "2024-01-05",
      comments: 5,
    },
    {
      id: 3,
      title: "Add more chart types",
      type: "suggestion",
      status: "planned",
      date: "2024-01-02",
      comments: 12,
    },
    {
      id: 4,
      title: "Mobile app crashes on launch",
      type: "bug",
      status: "reviewing",
      date: "2023-12-28",
      comments: 8,
    },
  ];

  const popularSuggestions = [
    {
      title: "Real-time notifications",
      votes: 156,
      status: "planned",
    },
    {
      title: "Custom report templates",
      votes: 89,
      status: "in-progress",
    },
    {
      title: "Multi-language support",
      votes: 67,
      status: "under-review",
    },
    {
      title: "Advanced filtering options",
      votes: 45,
      status: "planned",
    },
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "bug":
        return <BugAntIcon className="h-5 w-5 text-red-500" />;
      case "suggestion":
        return <LightBulbIcon className="h-5 w-5 text-yellow-500" />;
      case "question":
        return <QuestionMarkCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "resolved":
        return "bg-green-100 text-green-800";
      case "in-progress":
        return "bg-blue-100 text-blue-800";
      case "planned":
        return "bg-purple-100 text-purple-800";
      case "reviewing":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log({
      feedbackType,
      title,
      description,
      category,
      priority,
      email,
      includeScreenshot,
    });
    setIsSubmitted(true);
    // Reset form
    setTimeout(() => {
      setTitle("");
      setDescription("");
      setCategory("");
      setPriority("medium");
      setEmail("");
      setIncludeScreenshot(false);
      setIsSubmitted(false);
    }, 5000);
  };

  const renderFeedbackForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Feedback Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          What type of feedback are you sharing?
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { id: "bug", label: "Bug Report", icon: BugAntIcon },
            {
              id: "suggestion",
              label: "Suggestion",
              icon: LightBulbIcon,
            },
            {
              id: "question",
              label: "Question",
              icon: QuestionMarkCircleIcon,
            },
            { id: "other", label: "Other", icon: ChatBubbleLeftRightIcon },
          ].map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => setFeedbackType(type.id as any)}
                className={`p-4 border rounded-lg text-center transition-all ${
                  feedbackType === type.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                }`}
              >
                <Icon
                  className={`h-6 w-6 mx-auto mb-2 ${
                    feedbackType === type.id ? "text-blue-600" : "text-gray-400"
                  }`}
                />
                <span
                  className={`text-sm font-medium ${
                    feedbackType === type.id ? "text-blue-700" : "text-gray-700"
                  }`}
                >
                  {type.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          placeholder="Briefly describe your feedback"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          placeholder="Please provide detailed information about your feedback..."
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <div className="relative">
            <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority
          </label>
          <div className="flex space-x-3">
            {[
              { id: "low", label: "Low", color: "bg-gray-100 text-gray-800" },
              {
                id: "medium",
                label: "Medium",
                color: "bg-blue-100 text-blue-800",
              },
              { id: "high", label: "High", color: "bg-red-100 text-red-800" },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPriority(p.id as any)}
                className={`flex-1 px-4 py-2 border rounded-lg transition-all ${
                  priority === p.id
                    ? "border-blue-500"
                    : "border-gray-300 hover:border-gray-400"
                } ${p.color}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Contact Email (Optional)
        </label>
        <div className="relative">
          <UserCircleIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            placeholder="your@email.com"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          We'll only use this to follow up on your feedback
        </p>
      </div>

      {/* Screenshot Option */}
      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          id="screenshot"
          checked={includeScreenshot}
          onChange={(e) => setIncludeScreenshot(e.target.checked)}
          className="h-4 w-4 text-blue-600 rounded"
        />
        <label htmlFor="screenshot" className="text-sm text-gray-700">
          Include a screenshot (you'll be prompted after submission)
        </label>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PaperAirplaneIcon className="h-5 w-5" />
          <span>Send Feedback</span>
        </button>
      </div>
    </form>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-normal text-gray-900">Send Feedback</h1>
          <p className="text-sm text-gray-600 mt-1">
            Help us improve AdSense Clone
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1 text-gray-600">
            <ClockIcon className="h-5 w-5" />
            <span className="text-sm">Response time: 2-3 business days</span>
          </div>
          <button className="text-sm text-blue-600 hover:text-blue-700">
            View feedback guidelines
          </button>
        </div>
      </div>

      {isSubmitted ? (
        /* Success Message */
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="text-center">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Feedback Submitted!
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Thank you for sharing your feedback. Our team will review it and
              get back to you if we need more information.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setIsSubmitted(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Submit Another
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                View Status
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Main Content */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Feedback Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Share Feedback</h3>
                  <p className="text-sm text-gray-600">
                    Your input helps us improve
                  </p>
                </div>
              </div>

              {renderFeedbackForm()}
            </div>

            {/* Previous Feedback */}
            <div className="bg-white rounded-lg border border-gray-200 mt-6">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">
                  Your Previous Feedback
                </h3>
                <p className="text-sm text-gray-600">
                  Track the status of your submissions
                </p>
              </div>

              <div className="divide-y divide-gray-200">
                {previousFeedback.map((item) => (
                  <div key={item.id} className="p-4">
                    <div className="flex items-start space-x-3">
                      {getTypeIcon(item.type)}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-gray-900">
                            {item.title}
                          </h4>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${getStatusColor(
                              item.status
                            )}`}
                          >
                            {item.status}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="capitalize">{item.type}</span>
                          <span>•</span>
                          <span>{item.date}</span>
                          <span>•</span>
                          <span>{item.comments} comments</span>
                        </div>
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700">
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {previousFeedback.length === 0 && (
                <div className="p-8 text-center">
                  <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Previous Feedback
                  </h3>
                  <p className="text-gray-600">
                    You haven't submitted any feedback yet
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Popular Suggestions */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <LightBulbIcon className="h-6 w-6 text-yellow-500" />
                <div>
                  <h3 className="font-medium text-gray-900">
                    Popular Suggestions
                  </h3>
                  <p className="text-sm text-gray-600">Vote on ideas</p>
                </div>
              </div>

              <div className="space-y-4">
                {popularSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="p-3 border border-gray-200 rounded-lg hover:border-blue-300"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900">
                        {suggestion.title}
                      </h4>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${getStatusColor(
                          suggestion.status
                        )}`}
                      >
                        {suggestion.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1 text-gray-500">
                        <StarIcon className="h-4 w-4" />
                        <span className="text-sm">
                          {suggestion.votes} votes
                        </span>
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700">
                        +1
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button className="w-full mt-4 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
                View all suggestions
              </button>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <ExclamationCircleIcon className="h-6 w-6 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-2">
                    Feedback Tips
                  </h4>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li className="flex items-start">
                      <span className="inline-block h-1.5 w-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></span>
                      <span>Be specific and include examples</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block h-1.5 w-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></span>
                      <span>Include URLs or screenshots when relevant</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block h-1.5 w-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></span>
                      <span>Check existing suggestions before submitting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block h-1.5 w-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></span>
                      <span>One idea per submission</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Status Legend */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-medium text-gray-900 mb-3">Status Legend</h4>
              <div className="space-y-2">
                {[
                  { status: "reviewing", label: "Under review by our team" },
                  { status: "planned", label: "Added to our roadmap" },
                  { status: "in-progress", label: "Currently being worked on" },
                  { status: "resolved", label: "Implemented or fixed" },
                ].map((item) => (
                  <div
                    key={item.status}
                    className="flex items-center space-x-2"
                  >
                    <span
                      className={`h-3 w-3 rounded-full ${
                        getStatusColor(item.status).split(" ")[0]
                      }`}
                    ></span>
                    <span className="text-sm text-gray-600">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Community Stats */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <ArrowPathIcon className="h-6 w-6 text-green-500" />
                <div>
                  <h4 className="font-medium text-gray-900">
                    Community Impact
                  </h4>
                  <p className="text-sm text-gray-600">
                    Your contributions matter
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    Feedback submitted
                  </span>
                  <span className="font-medium">12</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    Suggestions upvoted
                  </span>
                  <span className="font-medium">156</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    Features influenced
                  </span>
                  <span className="font-medium">3</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-green-50 rounded border border-green-100">
                <p className="text-sm text-green-700">
                  Your feedback helped improve our dashboard design!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;
